<template>
  <Head>
    <Title v-if="title">{{ title }}</Title>
    <Meta name="og:image" :content="staticImageSRC" />
  </Head>
  <div class="wrapper">
    <Rewind
      :route="routeAsGeoJSON"
      :trip="{ user_id: '1', main_section: { photos: [], distance: distance } }"
      :unit="unit"
    />
  </div>
</template>

<script lang="ts">
import { decode } from '@mapbox/polyline'
import { type SavedRoute, type Route } from '~/types/route'
import RoutingMode, { type Waypoint } from '~/types/types'

function generateID() {
  return '_' + Math.random().toString(36).substring(2, 9)
}

export default {
  name: 'RoutePlanner',
  async setup() {
    definePageMeta({
      layout: 'empty'
    })

    const route = useRoute()
    const { getRoute } = useRoutingService()

    const search = route.query
    const unit = route.query.unit ? route.query.unit : 'metric'

    const routing = search.routing || 'curvature'
    let routeOwner
    const title = ''
    let routeID
    let routes: Array<Route> = []
    const planned: Array<SavedRoute> = []
    let waypoints: Array<Waypoint> = [{ id: generateID() }, { id: generateID() }]
    const mode = RoutingMode.A2B
    if (search.waypoints) {
      waypoints = (search.waypoints as string)
        .split(';')
        .map((waypoint) => waypoint.split(','))
        .map((waypoint) => ({ id: generateID(), position: waypoint.map(Number) }))
    }

    if (waypoints.filter((wp) => wp.position).length > 1) {
      try {
        routes = await getRoute(waypoints, routing as string, '0', mode, '')
      } catch (err) {
        console.error(err)
      }
    }

    if (waypoints.length === 1) {
      waypoints.push({ id: generateID() })
    }
    return {
      unit,
      routes: ref(routes),
      waypoints: ref(waypoints),
      routing: ref(routing),
      selectedRouteIndex: 0,
      routeOwner,
      title: ref(title),
      routeID,
      planned: ref(planned),
      mode: ref(mode)
    }
  },
  data() {
    return {
      mapStyle: 'map'
    }
  },
  computed: {
    staticImageSRC() {
      const config = useRuntimeConfig()
      if (this.routes.length === 0) {
        return ''
      }
      const polyline = this.selectedRoute ? `polyline=${this.selectedRoute.geometry}&` : ''
      return `${config.public.mapRenderingUrl}?${polyline}size=1200x630`
    },
    waypointsWithPosition() {
      return this.waypoints.filter((waypoint: Waypoint) => waypoint && waypoint.position)
    },
    selectedRoute() {
      const route = this.routes[this.selectedRouteIndex]
      return route
    },
    distance() {
      const route = this.routes[this.selectedRouteIndex]
      return route.distance
    },
    routeAsGeoJSON() {
      const route = this.selectedRoute
      const coordinates = decode(route.geometry, 6)
      return {
        features: [
          {
            geometry: {
              coordinates: coordinates.map((e) => e.reverse())
            }
          }
        ]
      }
    },
    isLoggedIn() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.session_data.user
    }
  },
  created() {
    const route = useRoute()
    watch(
      () => route.query,
      async () => {
        const route = useRoute()
        const search = route.query
        this.routing = search.routing || 'curvature'

        if (search.waypoints) {
          this.waypoints = (search.waypoints as string)
            .split(';')
            .map((waypoint: string) => waypoint.split(','))
            .map((waypoint: Array<any>) => {
              const position = waypoint.map(Number)
              const wp = this.waypoints.find(
                (wp: Waypoint) => JSON.stringify(wp.position) === JSON.stringify(position)
              )
              const id = wp ? wp.id : generateID()
              return { id, position }
            })
        }
        if (this.waypointsWithPosition.length > 1) {
          this.getRoutes()
        } else if (this.waypoints.length < 2) {
          this.waypoints.push({ id: generateID() })
          this.routes = []
          this.selectedRouteIndex = -1
        }
      }
    )
  },
  methods: {
    routeClick(route: Route) {
      this.selectedRouteIndex = this.routes.findIndex((r: Route) => r === route)
    },

    async getRoutes() {
      const { getRoute } = useRoutingService()
      try {
        const routes = await getRoute(
          this.waypoints,
          this.routing as string,
          '0',
          this.mode,
          ''
        )
        this.selectedRouteIndex = routes.findIndex((route) => route !== null)
        this.routes = routes
      } catch {
        alert('Could not find route')
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.wrapper
  height: 100vh
</style>
